/* --- START: src/components/HistoryModal.js --- */
import React, { useState, useMemo } from 'react';
import './HistoryModal.css';
import RuleEditForm from './RuleEditForm';

const getScoreColor = (score) => {
    if (score >= 4) return 'var(--success-color)';
    if (score >= 3) return 'var(--refine-color)';
    if (score === 2) return 'var(--flagged-color)';
    return 'var(--error-color)';
};

function HistoryModal({ rule: initialRule, onClose, onUpdateRule, onDeleteRule, allRules, startInEditMode, onToggleFlag, onReportIssue }) {
  const [viewMode, setViewMode] = useState(startInEditMode ? 'edit' : 'view'); 
  const [selectedAttempt, setSelectedAttempt] = useState(null); 
  const [activeTooltip, setActiveTooltip] = useState(null);
  
  const [isQuickAdding, setIsQuickAdding] = useState(false);
  const [quickKeynoteText, setQuickKeynoteText] = useState('');

  const rule = useMemo(() => {
    if (allRules && initialRule) {
      const found = allRules.find(r => r.id === initialRule.id);
      if (found) return found;
    }
    return initialRule;
  }, [allRules, initialRule]);

  const formattedContent = useMemo(() => {
    if (!rule || !rule.content_html) return '';
    return rule.content_html.replace(/\[(.*?)\]/g, (match, innerText) => {
      return `<span data-cloze>${innerText}</span>`;
    });
  }, [rule]);

  const handleSaveEdit = (updatedRule) => { 
      onUpdateRule(updatedRule); 
      setViewMode('view'); 
  };
  
  const handleFlagClick = () => { if (onToggleFlag) onToggleFlag(rule.id); };

  const handleQuickSaveKeynote = () => {
      if (!quickKeynoteText.trim()) return;
      onUpdateRule({ ...rule, keynote: quickKeynoteText });
      setIsQuickAdding(false);
      setQuickKeynoteText('');
  };

  const handleInteraction = (e, feedbackData) => {
      const marker = e.target.closest('span[id]');
      if (!marker) {
          if (e.type === 'click') setActiveTooltip(null);
          return;
      }
      e.stopPropagation();
      const id = parseInt(marker.getAttribute('id'), 10);
      const comments = feedbackData.comments || [];
      const comment = comments.find(c => c.id === id);
      
      if (comment) {
          const rect = marker.getBoundingClientRect();
          setActiveTooltip({
              id,
              text: comment.feedback,
              x: rect.left + (rect.width / 2),
              y: rect.top - 10
          });
      }
  };

  const renderAttemptDetail = () => {
      const attempt = selectedAttempt;
      if (!attempt) return null;

      const feedbackData = attempt.feedback || attempt.feedback_data || {};
      const hasAIFeedback = !!feedbackData.annotated_user_answer_html;
      const modelAnswer = feedbackData.model_answer;
      const processedHtml = hasAIFeedback 
          ? feedbackData.annotated_user_answer_html.replace(/<span id="/g, '<span class="ai-annotation" id="')
          : '';

      return (
          <div className="history-detail-view" onClick={() => setActiveTooltip(null)}>
              <div className="detail-header">
                  <button className="back-link" onClick={() => { setSelectedAttempt(null); setActiveTooltip(null); }}>
                      <span className="material-symbols-outlined">arrow_back</span> Back to List
                  </button>
                  <div className="detail-meta">
                      <span className="date">{new Date(attempt.timestamp).toLocaleDateString()}</span>
                      <span className="score-badge" style={{backgroundColor: getScoreColor(attempt.userScore)}}>{attempt.userScore}/5</span>
                  </div>
              </div>
              
              {activeTooltip && (
                  <div 
                      className="modal-tooltip-box visible" 
                      style={{ 
                          position: 'fixed', 
                          left: `${activeTooltip.x}px`, 
                          top: `${activeTooltip.y}px`,
                          transform: 'translate(-50%, -100%)',
                          marginTop: '-10px'
                      }}
                      onClick={(e) => e.stopPropagation()}
                  >
                      {activeTooltip.text}
                      <div className="tooltip-arrow"></div>
                  </div>
              )}

              <div className="detail-split-container">
                  {hasAIFeedback ? (
                      <>
                          <div className="detail-panel model-side">
                              <h4 className="panel-label">Model Analysis</h4>
                              <div className="scroll-area">
                                  {modelAnswer ? (
                                      <>
                                          <div className="model-block"><strong>Issue:</strong> <p>{modelAnswer.issue}</p></div>
                                          <div className="model-block"><strong>Rule:</strong> <p>{modelAnswer.rule}</p></div>
                                          <div className="model-block"><strong>Analysis:</strong> <p>{modelAnswer.analysis}</p></div>
                                          <div className="model-block"><strong>Conclusion:</strong> <p>{modelAnswer.conclusion}</p></div>
                                      </>
                                  ) : <p className="text-muted">No model answer provided.</p>}
                              </div>
                          </div>
                          
                          <div className="detail-panel user-side" onClick={(e) => handleInteraction(e, feedbackData)}>
                              <h4 className="panel-label">Your Answer (Annotated)</h4>
                              <div className="scroll-area">
                                  <div 
                                      className="annotated-content" 
                                      dangerouslySetInnerHTML={{ __html: processedHtml }} 
                                  />
                              </div>
                          </div>
                      </>
                  ) : (
                      <div className="detail-panel full-width">
                          <h4 className="panel-label">Your Answer</h4>
                          <div className="scroll-area user-raw-text">
                              {typeof attempt.userAttempt === 'string' ? attempt.userAttempt : "No text recorded."}
                          </div>
                      </div>
                  )}
              </div>
          </div>
      );
  };

  const renderHistoryList = () => {
      const attempts = rule.attempt_history || [];
      if (attempts.length === 0) {
          return (
              <div className="empty-history-state">
                  <span className="material-symbols-outlined" style={{fontSize: '3em', color: 'var(--border-color)'}}>history_edu</span>
                  <p>No attempts recorded for this rule yet.</p>
              </div>
          );
      }
      return (
          <div className="history-list-container">
              <table className="history-table">
                  <thead>
                      <tr><th>Date</th><th>Type</th><th>Score</th><th>Snippet</th><th></th></tr>
                  </thead>
                  <tbody>
                      {attempts.map((attempt, idx) => {
                          const date = new Date(attempt.timestamp).toLocaleDateString();
                          const hasFeedback = !!(attempt.feedback || attempt.feedback_data);
                          const snippet = typeof attempt.userAttempt === 'string' 
                            ? (attempt.userAttempt.substring(0, 30) + "...") 
                            : "Done";

                          return (
                              <tr key={idx} onClick={() => setSelectedAttempt(attempt)} className="clickable-row">
                                  <td>{date}</td>
                                  <td style={{textTransform: 'capitalize'}}>{attempt.source}</td>
                                  <td><span className="score-badge" style={{backgroundColor: getScoreColor(attempt.userScore)}}>{attempt.userScore}/5</span></td>
                                  <td className="feedback-preview">
                                      {hasFeedback ? <span className="ai-tag"><span className="material-symbols-outlined icon-tiny">assignment_turned_in</span> Graded by Wali's</span> : snippet}
                                  </td>
                                  <td><span className="material-symbols-outlined chevron">chevron_right</span></td>
                              </tr>
                          );
                      })}
                  </tbody>
              </table>
          </div>
      );
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="history-modal theme-aware" onClick={(e) => e.stopPropagation()}>
        <div className="modal-toolbar-pane">
           <div className="toolbar-context">
              <span className="material-symbols-outlined">folder_open</span>
              <span>{(rule.displayPath && rule.displayPath.length > 0) ? rule.displayPath.join(' / ') : 'General'}</span>
           </div>
          <div className="modal-actions">
            {!selectedAttempt && viewMode !== 'edit' && (
                <div className="view-toggles">
                    <button className={`icon-btn ${viewMode === 'view' ? 'active-view' : ''}`} onClick={() => setViewMode('view')} title="Black Letter Law"><span className="material-symbols-outlined">menu_book</span></button>
                    <button className={`icon-btn ${viewMode === 'history' ? 'active-view' : ''}`} onClick={() => setViewMode('history')} title="Attempt History"><span className="material-symbols-outlined">history</span></button>
                </div>
            )}
            {!selectedAttempt && viewMode !== 'edit' && (
              <>
                <button className={`icon-btn flag-btn ${rule.isFlagged ? 'active' : ''}`} onClick={handleFlagClick} title={rule.isFlagged ? 'Unflag Rule' : 'Flag Rule'}><span className="material-symbols-outlined">flag</span></button>
                {onReportIssue && <button className="icon-btn" onClick={() => onReportIssue(rule.id)} title="Report Issue"><span className="material-symbols-outlined">bug_report</span></button>}
                <button className="icon-btn" onClick={() => setViewMode('edit')} title="Edit Rule & Keynote"><span className="material-symbols-outlined">edit_note</span></button>
              </>
            )}
            <button className="icon-btn close-btn" onClick={onClose}><span className="material-symbols-outlined">close</span></button>
          </div>
        </div>

        <div className="modal-content-pane">
          {viewMode === 'edit' ? (
            <div className="rule-editor-scroll-area">
              <RuleEditForm rule={rule} allRules={allRules} onSave={handleSaveEdit} onCancel={() => setViewMode('view')} onDelete={onDeleteRule} />
            </div>
          ) : viewMode === 'history' ? (
             <div className="rule-detail-view history-mode">
                 {!selectedAttempt && (
                     <header className="document-header">
                        <h1 className="document-title">Performance History</h1>
                        <p style={{color: 'var(--text-muted)', margin: 0}}>Track record for: {rule.ruleName}</p>
                     </header>
                 )}
                 {selectedAttempt ? renderAttemptDetail() : renderHistoryList()}
             </div>
          ) : (
            <div className="rule-detail-view">
              <header className="document-header">
                <h1 className="document-title">{rule.ruleName}</h1>
<div className="mastery-dashboard">
                 <div className="mastery-row">
                     <div className="mastery-label">SRS Interval</div>
                     <div className="mastery-track">
                         <div 
                            className="mastery-fill" 
                            style={{width: `${Math.min(100, ((rule.srs_data?.currentInterval || 0) / 21) * 100)}%`}}
                         ></div>
                     </div>
                     <div className="mastery-value">{(rule.srs_data?.currentInterval || 0)} / 21 Days</div>
                 </div>
                 
                 {rule.isHFR && (
                     <div className="mastery-row">
                         <div className="mastery-label">Application</div>
                         <div className={`mastery-check ${rule.attempt_history?.some(a => a.source === 'quiz' && a.userScore >= 2) ? 'passed' : 'pending'}`}>
                             {rule.attempt_history?.some(a => a.source === 'quiz' && a.userScore >= 2) 
                                ? <><span className="material-symbols-outlined icon-tiny">check_circle</span> Quiz Passed</> 
                                : <><span className="material-symbols-outlined icon-tiny">radio_button_unchecked</span> Quiz Needed</>
                             }
                         </div>
                     </div>
                 )}
                 
                 <div className="mastery-status-chip">
                     {/* Restored Indicators */}
                     {rule.isHFR && <span className="status-pill hfr">HFR</span>}
                     {rule.isFlagged && <span className="status-pill flagged">Flagged</span>}
                     
                     <span className={`status-pill ${rule.status || 'not_started'}`}>
                        {rule.status === 'mastered' ? 'MASTERED' : rule.status === 'srs_mastered' ? 'SRS MAXED' : 'IN PROGRESS'}
                     </span>
                 </div>
              </div>
              </header>
              <section className="content-section paper-section">
                <h4 className="section-label">Black Letter Law</h4>
                <div className="rule-content-display typography-content" dangerouslySetInnerHTML={{ __html: formattedContent }} />
              </section>

              <section className="content-section keynote-section">
                {rule.keynote ? (
                   <div className="keynote-paper">
                        <div className="keynote-header-row">
                            <h4 className="section-label keynote-label"><span className="material-symbols-outlined">lightbulb</span> My Keynote</h4>
                            <button className="icon-btn simple-edit" onClick={() => setViewMode('edit')} title="Edit Keynote">
                                <span className="material-symbols-outlined">edit</span>
                            </button>
                        </div>
                      <div className="keynote-text">{rule.keynote}</div>
                   </div>
                ) : (
                    isQuickAdding ? (
                        <div className="quick-add-keynote-container">
                             <h4 className="section-label"><span className="material-symbols-outlined">lightbulb</span> New Keynote</h4>
                             <textarea 
                                className="quick-keynote-input"
                                placeholder="Type your mnemonic or memory aid here..."
                                autoFocus
                                value={quickKeynoteText}
                                onChange={(e) => setQuickKeynoteText(e.target.value)}
                             />
                             <div className="quick-add-actions">
                                 <button className="btn small-btn cancel" onClick={() => setIsQuickAdding(false)}>Cancel</button>
                                 <button className="btn small-btn save" onClick={handleQuickSaveKeynote}>Save Keynote</button>
                             </div>
                        </div>
                    ) : (
                        <button className="add-keynote-placeholder-btn" onClick={() => setIsQuickAdding(true)}>
                            <span className="material-symbols-outlined">add_notes</span>
                            Add a Personal Keynote
                        </button>
                    )
                )}
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default HistoryModal;